import { useState } from "react";
import { toast } from "react-toastify";
import { TextField } from "@mui/material";

import CustomModal from "../../Components/Shared/ModalCustom/CustomModal";
import ConfirmModal from "../../Components/Shared/ModalCustom/ConfirmModal";
import { idColumn, nameColumn } from "../../Components/Shared/TableColumns/tableColumns";
import { useApiData } from "../../Hooks/useApiData";
import {
  addPermission,
  deletePermission,
  getPermissions,
  updatePermission,
} from "../../Service/PermissionService/PermissionService";
import { apiErrorStatus, handleApiError } from "../../Utils/apiErrors";
import { MANAGE_USERS, permissionLabel } from "../../Utils/permissionsUtils";
import SearchableTable from "./SearchableTable";
import { UsersTabProps } from "./userUtils";

// gestionar_usuarios no se puede renombrar ni borrar (el back responde 409)
const isProtected = (permission: any) => permission?.name === MANAGE_USERS;

function PermissionFormModal(props: { permission: any; onClose: () => void; onChanged: () => void }) {
  const { permission, onClose, onChanged } = props;
  const isEdit = permission !== null;
  const [name, setName] = useState<string>(permission?.name ?? "");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const save = () => {
    const value = name.trim();
    if (value === "") {
      setError("Este campo es requerido");
      return;
    }
    if (isEdit && value === permission.name) {
      onClose();
      return;
    }
    setSaving(true);
    (isEdit ? updatePermission(permission.id, { name: value }) : addPermission({ name: value }))
      .then(() => {
        toast.success(isEdit ? "Permiso actualizado" : "Permiso creado");
        onChanged();
        onClose();
      })
      .catch((err) =>
        handleApiError(err, {
          fallback: "No se ha podido guardar el permiso",
          setFieldErrors: (errors) => setError(errors.name ?? null),
          onNotFound: () => {
            onChanged();
            onClose();
          },
        })
      )
      .finally(() => setSaving(false));
  };

  return (
    <CustomModal
      size="md"
      show
      onChange={onClose}
      title={isEdit ? "Editar permiso" : "Añadir permiso"}
      body={
        <form
          className="aedasForm aedasForm--single"
          onSubmit={(e) => {
            e.preventDefault();
            save();
          }}
          noValidate
        >
          <TextField
            id="permission-name"
            label="Nombre"
            required
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setError(null);
            }}
            error={Boolean(error)}
            helperText={error ?? "En minúsculas y con guiones bajos, p. ej. gestionar_usuarios"}
          />
        </form>
      }
      buttons={[
        { content: "Cancelar", primary: false, action: onClose },
        { content: "Guardar", primary: true, action: save, disabled: saving },
      ]}
    />
  );
}

// Pestaña Permisos: listado (GET /permission), alta, renombrado y borrado
export default function PermissionsTab(props: UsersTabProps) {
  const { createOpen, onCreateClose, onSelfChanged } = props;

  const { data: permissions, loading, reload } = useApiData<any[]>(getPermissions);
  const [editing, setEditing] = useState<any>(null);
  const [toDelete, setToDelete] = useState<any>(null);
  const [deleting, setDeleting] = useState(false);

  // Renombrar o borrar un permiso puede cambiar los del usuario actual
  const changed = () => {
    reload();
    onSelfChanged();
  };

  const remove = () => {
    setDeleting(true);
    deletePermission(toDelete.id)
      .then(() => {
        toast.success("Permiso eliminado");
        setToDelete(null);
        changed();
      })
      .catch((error) => {
        handleApiError(error, { fallback: "No se ha podido eliminar el permiso", onNotFound: reload });
        if (apiErrorStatus(error) === 404) setToDelete(null);
      })
      .finally(() => setDeleting(false));
  };

  const columns = [
    idColumn(),
    nameColumn("label", "Permiso", {
      valueGetter: (params) => permissionLabel(params.row.name),
      searchValue: (row) => permissionLabel(row.name),
    }),
    nameColumn("name", "Clave"),
  ];

  return (
    <>
      <SearchableTable
        rows={permissions}
        columns={columns}
        loading={loading}
        onEdit={setEditing}
        onDelete={setToDelete}
        canEdit={(row) => !isProtected(row)}
        canDelete={(row) => !isProtected(row)}
      />

      {createOpen || editing ? (
        <PermissionFormModal
          permission={editing}
          onClose={() => {
            setEditing(null);
            onCreateClose();
          }}
          onChanged={changed}
        />
      ) : null}

      <ConfirmModal
        show={toDelete !== null}
        title="Eliminar permiso"
        message={`Se quitará el permiso "${permissionLabel(toDelete?.name ?? "")}" de todos los roles que lo tienen asignado. Esta acción no se puede deshacer.`}
        confirmLabel="Eliminar"
        onConfirm={remove}
        onCancel={() => setToDelete(null)}
        disabled={deleting}
      />
    </>
  );
}
